import { useEffect, useId, useState, type CSSProperties, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { capitalize } from "lodash";
import Avatar from "@/components/Avatar/Avatar";
import ProfilePhotoPreviewDialog from "@/features/person/components/ProfilePhotoPreviewDialog/ProfilePhotoPreviewDialog";
import Tag from "@/components/Tag";
import CopyButton from "@/components/CopyButton";
import IconButton from "@/components/IconButton";
import Button from "@/components/Button";
import Icon from "@/components/Icon";
import Dialog from "@/components/Dialog";
import DetailsCard from "@/components/DetailsCard/DetailsCard";
import Tooltip from "@/components/Tooltip";
import Menu from "@/components/Menu";
import { DEFAULT_MENU_BUTTON_TEST_ID, toMenuListEntries, type Action } from "@/components/MenuButton";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { renderName } from "@/lib/personHelpers";
import { m360Paths } from "@/lib/m360Paths";
import { toFormat } from "@/lib/dateHelpers";
import { formatNotePriority } from "@/features/global/components/NotesTable/noteHelpers";
import type { Path } from "@/router";
import type { PinnedNotesConfig } from "@/components/ObjectHeading";
import { CRMNote, NotePriorityValues, Person, ProgramTypeName } from "@/types";
import { getProgramStatus, getPrimarySectionName } from "@/pages/people/_utils";
import { getCurrentPrimaryEmployment, getDisplayedCcmsSuspension, getSuspensionReasonLabel } from "../../utils/utils";
import {
  ClassFTag,
  NO_PRIMARY_EMPLOYMENT_ON_RECORD,
  ProgramStatusTag,
  createClassificationInfo,
  createFacilityInfo,
  getMostRecentEndedEmploymentTooltip,
  hasActiveClassFBand,
  shouldShowPersonHeadingEmployment,
  shouldShowPersonHeadingEmploymentEmptyState
} from "./personObjectHeadingHelpers";

const IDENTITY_LINK_CLASS_NAME =
  "no-underline cursor-pointer hover:underline focus-visible:underline underline-offset-2";

const NAME_TEXT_CLASS_NAME = "text-title-large-bold font-serif text-sys-brand-on-surface";

const NAME_LINK_CLASS_NAME = `${NAME_TEXT_CLASS_NAME} hover:text-sys-brand-on-surface focus-visible:text-sys-brand-on-surface ${IDENTITY_LINK_CLASS_NAME}`;

const METADATA_TEXT_CLASS_NAME = "text-sys-brand-on-surface";

const METADATA_LINK_CLASS_NAME = `${METADATA_TEXT_CLASS_NAME} hover:text-sys-brand-on-surface focus-visible:text-sys-brand-on-surface ${IDENTITY_LINK_CLASS_NAME}`;

const ADMIN_OVERFLOW_TEXTS = new Set(["CCMS", "View Student Profile"]);

const NOTES_LINK_COLOR = "var(--md-sys-color-on-surface)";

const NOTES_LINK_CLASS_NAME =
  "group inline-flex [&_md-text-button]:[outline:none] [&_md-text-button]:shadow-none [&_md-text-button::part(focus-ring)]:hidden";

const DETAILS_TOGGLE_CLASS_NAME = "group self-start";

const NOTES_LABEL_CLASS_NAME =
  "text-sys-brand-on-surface underline-offset-2 group-hover:underline group-focus-within:underline";

const DETAILS_TOGGLE_COLOR = "var(--md-sys-color-primary)";

const DETAILS_TOGGLE_STYLE = {
  minWidth: 0,
  paddingInlineStart: 0,
  paddingInlineEnd: 0,
  justifyContent: "flex-start",
  placeContent: "flex-start",
  gap: 0,
  "--md-text-button-container-height": "16px",
  "--md-text-button-label-text-size": "11px",
  "--md-text-button-label-text-line-height": "16px",
  "--md-text-button-label-text-weight": "500",
  "--md-text-button-leading-space": "0px",
  "--md-text-button-trailing-space": "0px",
  "--md-text-button-with-trailing-icon-leading-space": "0px",
  "--md-text-button-with-trailing-icon-trailing-space": "0px",
  "--md-text-button-icon-size": "16px",
  "--md-text-button-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-hover-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-focus-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-pressed-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-hover-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-focus-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-pressed-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-hover-state-layer-opacity": 0,
  "--md-text-button-pressed-state-layer-opacity": 0,
  "--md-text-button-hover-state-layer-color": "transparent",
  "--md-text-button-pressed-state-layer-color": "transparent",
  "--md-ripple-hover-opacity": 0,
  "--md-ripple-pressed-opacity": 0,
  "--md-ripple-hover-color": "transparent",
  "--md-ripple-pressed-color": "transparent"
} as CSSProperties;

function CompactDetailsToggle({
  detailsOpen,
  onToggle,
  compactDetailsId
}: {
  detailsOpen: boolean;
  onToggle: () => void;
  compactDetailsId: string;
}) {
  return (
    <span className={DETAILS_TOGGLE_CLASS_NAME} data-testid="person-heading-details-toggle-wrap">
      <Button
        variant="text"
        type="button"
        trailingIcon
        icon={detailsOpen ? "expand_less" : "expand_more"}
        ariaExpanded={detailsOpen}
        ariaControls={compactDetailsId}
        testId="person-heading-details-toggle"
        onClick={onToggle}
        style={DETAILS_TOGGLE_STYLE}
      >
        <span className="underline-offset-2 group-hover:underline group-focus-visible:underline group-focus-within:underline">
          {detailsOpen ? "Less" : "More"}
        </span>
      </Button>
    </span>
  );
}

const PROFILE_LINK_LABEL_CLASS_NAME =
  "text-sys-brand-primary underline-offset-2 group-hover:underline group-focus-within:underline";

/** Side sheet Open profile — body-medium link styling in brand primary. */
const PROFILE_LINK_STYLE = {
  minWidth: 0,
  gap: 0,
  "--md-text-button-container-height": "20px",
  "--md-text-button-label-text-size": "14px",
  "--md-text-button-label-text-line-height": "20px",
  "--md-text-button-label-text-weight": "400",
  "--md-text-button-leading-space": "0px",
  "--md-text-button-trailing-space": "0px",
  "--md-text-button-with-leading-icon-leading-space": "0px",
  "--md-text-button-with-leading-icon-trailing-space": "0px",
  "--md-text-button-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-hover-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-focus-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-pressed-label-text-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-hover-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-focus-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-pressed-icon-color": DETAILS_TOGGLE_COLOR,
  "--md-text-button-hover-state-layer-opacity": 0,
  "--md-text-button-pressed-state-layer-opacity": 0,
  "--md-text-button-hover-state-layer-color": "transparent",
  "--md-text-button-pressed-state-layer-color": "transparent",
  "--md-text-button-focus-outline-color": "transparent",
  "--md-focus-ring-color": "transparent",
  "--md-focus-ring-width": "0px",
  "--md-focus-ring-active-width": "0px",
  "--md-focus-ring-outward-offset": "0px",
  "--md-ripple-hover-opacity": 0,
  "--md-ripple-pressed-opacity": 0,
  "--md-ripple-hover-color": "transparent",
  "--md-ripple-pressed-color": "transparent"
} as CSSProperties;

/** Match job title `text-body-medium` (14/20). Hover underlines the label only — no Material ring. */
const NOTES_TEXT_LINK_STYLE = {
  minWidth: 0,
  gap: 0,
  "--md-text-button-container-height": "20px",
  "--md-text-button-label-text-size": "14px",
  "--md-text-button-label-text-line-height": "20px",
  "--md-text-button-label-text-weight": "400",
  "--md-text-button-leading-space": "0px",
  "--md-text-button-trailing-space": "0px",
  "--md-text-button-with-leading-icon-leading-space": "0px",
  "--md-text-button-with-leading-icon-trailing-space": "0px",
  "--md-text-button-label-text-color": NOTES_LINK_COLOR,
  "--md-text-button-hover-label-text-color": NOTES_LINK_COLOR,
  "--md-text-button-focus-label-text-color": NOTES_LINK_COLOR,
  "--md-text-button-pressed-label-text-color": NOTES_LINK_COLOR,
  "--md-text-button-icon-color": NOTES_LINK_COLOR,
  "--md-text-button-hover-icon-color": NOTES_LINK_COLOR,
  "--md-text-button-focus-icon-color": NOTES_LINK_COLOR,
  "--md-text-button-pressed-icon-color": NOTES_LINK_COLOR,
  "--md-text-button-hover-state-layer-opacity": 0,
  "--md-text-button-pressed-state-layer-opacity": 0,
  "--md-text-button-hover-state-layer-color": "transparent",
  "--md-text-button-pressed-state-layer-color": "transparent",
  "--md-text-button-focus-outline-color": "transparent",
  "--md-focus-ring-color": "transparent",
  "--md-focus-ring-width": "0px",
  "--md-focus-ring-active-width": "0px",
  "--md-focus-ring-outward-offset": "0px",
  "--md-ripple-hover-opacity": 0,
  "--md-ripple-pressed-opacity": 0,
  "--md-ripple-hover-color": "transparent",
  "--md-ripple-pressed-color": "transparent"
} as CSSProperties;

export const remappedPersonOverflowActions = (actions: Action[]): Action[] => {
  const rest: Action[] = [];
  const admin: Action[] = [];

  for (const action of actions) {
    if (ADMIN_OVERFLOW_TEXTS.has(action.text)) {
      admin.push({
        ...action,
        text: action.text === "CCMS" ? "View in CCMS" : action.text,
        iconName: "open_in_new",
        menuOnly: true,
        section: "Admin",
        dividerAbove: true
      });
    } else {
      rest.push({ ...action, menuOnly: true });
    }
  }

  return [...rest, ...admin];
};

export type PersonObjectHeadingRefreshProps = {
  person: Person;
  pgaId: string;
  canViewPersonCareer?: boolean;
  actionButtons?: Action[];
  menuButtonLabel?: string;
  linkIdentityChrome?: boolean;
  hideTags?: boolean;
  onImageEditClick?: () => void;
  pinnedNotes?: PinnedNotesConfig;
  isSticky?: boolean;
  initialNotesDialogOpen?: boolean;
  /** Storybook: force compact layout. Production omits this and uses `useIsCompactLayout`. */
  compact?: boolean;
  /** Storybook: start the compact details panel expanded. Production omits this and starts collapsed. */
  compactDetailsOpen?: boolean;
  /** Person side sheet: always show PGA ID, section, and optional employment without More/Less. */
  sideSheet?: boolean;
  /** Side sheet only: show job/facility in the header when the Employment section is hidden. */
  showEmploymentInHeader?: boolean;
  /** Side sheet only: show Open profile below PGA ID and section. */
  showProfileLink?: boolean;
  onOpenProfile?: () => void;
};

const IdentityValue = ({ href, className, children }: { href?: string; className: string; children: ReactNode }) => {
  if (href) {
    return (
      <Link to={href as Path} className={className}>
        {children}
      </Link>
    );
  }

  return <span className={className}>{children}</span>;
};

const ProgramTypeTag = ({ label }: { label: string }) => (
  <Tag tagType="outline" tagStyle="standard" size="large" compact>
    {label}
  </Tag>
);

const PgaIdText = ({ pgaId }: { pgaId: string }) => (
  <CopyButton
    textToCopy={pgaId}
    copyFieldLabel="PGA ID"
    copyFieldId={pgaId}
    showCopyIcon={false}
    className="group/copy"
  >
    <span className="text-body-medium text-sys-brand-on-surface group-hover/copy:underline group-focus-within/copy:underline underline-offset-2">
      PGA ID {pgaId}
    </span>
  </CopyButton>
);

const getSectionDisplay = (person: Person, linkIdentityChrome: boolean) => {
  const { latestProgram } = person;

  if (latestProgram?.programType.name === ProgramTypeName.Student) {
    const name = latestProgram.band?.section?.name || person.university?.name;
    return { name, href: undefined as string | undefined };
  }

  const name = getPrimarySectionName(person);
  const sectionId = person.primarySection?.id;
  return {
    name,
    href: linkIdentityChrome && sectionId ? m360Paths.section(sectionId) : undefined
  };
};

export const PersonObjectHeadingRefresh = ({
  person,
  pgaId,
  canViewPersonCareer = true,
  actionButtons,
  menuButtonLabel,
  linkIdentityChrome = false,
  hideTags = false,
  onImageEditClick,
  pinnedNotes,
  isSticky,
  initialNotesDialogOpen = false,
  compact,
  compactDetailsOpen,
  sideSheet = false,
  showEmploymentInHeader = true,
  showProfileLink = false,
  onOpenProfile
}: PersonObjectHeadingRefreshProps) => {
  const { isCompactLayout } = useIsCompactLayout();
  const useCompact = compact ?? isCompactLayout;
  const compactDetailsPanelId = useId();
  const compactDetailsId = `person-heading-compact-details${compactDetailsPanelId}`;
  const [notesDialogOpen, setNotesDialogOpen] = useState(initialNotesDialogOpen);
  const [profilePhotoPreviewOpen, setProfilePhotoPreviewOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(sideSheet || (compactDetailsOpen ?? false));
  const compactDetailsVisible = sideSheet || detailsOpen;

  useEffect(() => {
    if (initialNotesDialogOpen) {
      setNotesDialogOpen(true);
    }
  }, [initialNotesDialogOpen]);

  const { profilePhoto, displayName, informalName, preferredLastName, suffixName, middleName, firstName } = person;
  const title = renderName(displayName, informalName, preferredLastName, suffixName, middleName, firstName);
  const nameHref = linkIdentityChrome ? m360Paths.personOverview(pgaId) : undefined;

  const showHeadingEmployment =
    canViewPersonCareer && shouldShowPersonHeadingEmployment(person) && (!sideSheet || showEmploymentInHeader);
  const currentPrimaryEmployment = showHeadingEmployment ? getCurrentPrimaryEmployment(person.employments) : undefined;
  const jobInfo = currentPrimaryEmployment
    ? createClassificationInfo(person, pgaId, undefined, linkIdentityChrome, true)[0]
    : undefined;
  const facilityInfo = currentPrimaryEmployment ? createFacilityInfo(person, linkIdentityChrome, true)[0] : undefined;
  const showEmploymentEmptyState = showHeadingEmployment && shouldShowPersonHeadingEmploymentEmptyState(person);
  const emptyStateTooltip = showEmploymentEmptyState ? getMostRecentEndedEmploymentTooltip(person) : undefined;
  const section = getSectionDisplay(person, linkIdentityChrome);
  const programTypeName = person.latestProgram?.programType?.name;
  const programTagLabel = programTypeName ? capitalize(programTypeName) : undefined;
  const programStatus = getProgramStatus(person);
  const activeSuspension = getDisplayedCcmsSuspension(programStatus, person.ccmsSuspensions);
  const suspensionReason = activeSuspension ? getSuspensionReasonLabel(activeSuspension) : undefined;
  const showClassF = hasActiveClassFBand(person);
  const overflowActions = remappedPersonOverflowActions(actionButtons ?? []);
  const overflowMenuItems = toMenuListEntries(overflowActions);
  const hasPinnedNotes = pinnedNotes && pinnedNotes.notes.length > 0;
  const hideNotesButton = useCompact && isSticky;

  const statusTag =
    !hideTags && programStatus ? (
      <ProgramStatusTag
        status={programStatus}
        terminatedReason={person.latestProgram?.band?.terminatedReason}
        terminatesOn={person.latestProgram?.band?.terminatesOn}
        suspensionReason={suspensionReason}
        suspensionStartDate={activeSuspension?.startDate}
        pendingTerminatesOn={person.latestProgram?.band?.expiresOn}
        size="large"
        compact
      />
    ) : null;

  const programTag = !hideTags && programTagLabel ? <ProgramTypeTag label={programTagLabel} /> : null;
  const classFTag = !hideTags && showClassF ? <ClassFTag size="large" compact /> : null;

  const canPreviewProfilePhoto = Boolean(profilePhoto);
  const avatar = (
    <Avatar
      size="medium"
      picture={profilePhoto}
      {...(!profilePhoto && { iconName: "person" as const })}
      className="flex-shrink-0"
      onPictureClick={canPreviewProfilePhoto ? () => setProfilePhotoPreviewOpen(true) : undefined}
    />
  );

  const overflowMenuTitle = menuButtonLabel || "Contact";
  const overflow =
    overflowMenuItems.length > 0 ? (
      <Menu
        menuItems={overflowMenuItems}
        title={overflowMenuTitle}
        AnchorComponent={
          <IconButton
            name="more_vert"
            skipMargin
            ariaLabel={overflowMenuTitle}
            data-testid={DEFAULT_MENU_BUTTON_TEST_ID}
          />
        }
      />
    ) : null;

  const unemployedNode = showEmploymentEmptyState
    ? (() => {
        const value = (
          <span className="text-body-medium italic text-sys-brand-on-surface">{NO_PRIMARY_EMPLOYMENT_ON_RECORD}</span>
        );
        return emptyStateTooltip ? (
          <Tooltip
            variant="plain"
            color="black"
            supportingText={emptyStateTooltip}
            menuCorner="end-start"
            anchorCorner="start-start"
            compactHoverArea
          >
            {value}
          </Tooltip>
        ) : (
          value
        );
      })()
    : null;

  const jobNode =
    !showEmploymentEmptyState && jobInfo
      ? (() => {
          const jobValue = (
            <IdentityValue
              href={jobInfo.href}
              className={jobInfo.href ? METADATA_LINK_CLASS_NAME : METADATA_TEXT_CLASS_NAME}
            >
              {jobInfo.text}
            </IdentityValue>
          );
          return jobInfo.tooltipText && !jobInfo.href ? (
            <Tooltip
              variant="plain"
              color="black"
              supportingText={jobInfo.tooltipText}
              menuCorner="end-start"
              anchorCorner="start-start"
              compactHoverArea
            >
              {jobValue}
            </Tooltip>
          ) : (
            jobValue
          );
        })()
      : null;

  const facilityNode =
    !showEmploymentEmptyState && facilityInfo ? (
      <IdentityValue
        href={facilityInfo.href}
        className={facilityInfo.href ? METADATA_LINK_CLASS_NAME : METADATA_TEXT_CLASS_NAME}
      >
        {facilityInfo.text}
      </IdentityValue>
    ) : null;

  const employmentColumn = showEmploymentEmptyState
    ? unemployedNode
    : (jobNode || facilityNode) && (
        <div className="flex min-w-0 flex-col gap-0.5 text-body-medium">
          {jobNode}
          {facilityNode}
        </div>
      );

  const sectionNameNode = section.name ? (
    <IdentityValue
      href={section.href}
      className={
        section.href ? `text-body-medium ${METADATA_LINK_CLASS_NAME}` : "text-body-medium text-sys-brand-on-surface"
      }
    >
      {section.name}
    </IdentityValue>
  ) : null;

  const notesButton =
    hasPinnedNotes && !hideNotesButton ? (
      <span className={NOTES_LINK_CLASS_NAME}>
        <Button
          variant="text"
          ariaExpanded={notesDialogOpen}
          testId="person-heading-notes-link"
          onClick={() => setNotesDialogOpen(true)}
          style={NOTES_TEXT_LINK_STYLE}
        >
          <span className="inline-flex items-center gap-px">
            <Icon name="push_pin" filled size={14} className="text-sys-brand-on-surface" />
            <span className={NOTES_LABEL_CLASS_NAME} data-testid="person-heading-notes-label">
              {pinnedNotes.notes.length} {pinnedNotes.notes.length === 1 ? "Note" : "Notes"}
            </span>
          </span>
        </Button>
      </span>
    ) : null;

  const nameNode = (
    <IdentityValue href={nameHref} className={nameHref ? NAME_LINK_CLASS_NAME : NAME_TEXT_CLASS_NAME}>
      {title}
    </IdentityValue>
  );

  const programStatusTags =
    programTag || statusTag || classFTag ? (
      <div className="flex flex-nowrap items-center gap-2">
        {programTag}
        {statusTag}
        {classFTag}
      </div>
    ) : null;

  const identityLine = (
    <div className="flex min-w-0 flex-wrap items-center gap-2" data-testid="person-heading-identity-line">
      {nameNode}
      {programStatusTags}
      {notesButton}
    </div>
  );

  const compactTagRow =
    programStatusTags || notesButton ? (
      <div className="mt-1 flex min-w-0 flex-wrap items-center gap-2" data-testid="person-heading-compact-tags">
        {programStatusTags}
        {notesButton}
      </div>
    ) : null;

  const idAndSectionLine = (
    <div className="flex min-w-0 flex-wrap items-center gap-2">
      <PgaIdText pgaId={pgaId} />
      {sectionNameNode ? (
        <>
          <span className="text-sys-brand-outline-variant" aria-hidden="true">
            •
          </span>
          {sectionNameNode}
        </>
      ) : null}
    </div>
  );

  const profileLinkButton =
    sideSheet && showProfileLink && onOpenProfile ? (
      <Button
        variant="text"
        icon="open_in_new"
        onClick={onOpenProfile}
        testId="person-side-sheet-open-profile"
        style={PROFILE_LINK_STYLE}
      >
        <span className={PROFILE_LINK_LABEL_CLASS_NAME}>Open profile</span>
      </Button>
    ) : null;

  const trailingCluster = overflow ? <div className="flex flex-shrink-0 items-center gap-2">{overflow}</div> : null;

  const detailsToggle =
    useCompact && !sideSheet ? (
      <CompactDetailsToggle
        detailsOpen={detailsOpen}
        onToggle={() => setDetailsOpen((open) => !open)}
        compactDetailsId={compactDetailsId}
      />
    ) : null;

  const renderNotesDialog = () => {
    if (!hasPinnedNotes) return null;

    return (
      <Dialog
        headline={`Pinned Notes (${pinnedNotes.notes.length})`}
        open={notesDialogOpen}
        onClosed={() => setNotesDialogOpen(false)}
        className="max-h-[85vh] w-[640px]"
        hasFormElements
        actions={
          <div className="flex w-full justify-between items-center">
            <div>
              {pinnedNotes.onViewAll && (
                <Button
                  variant="text"
                  onClick={() => {
                    pinnedNotes.onViewAll?.();
                    setNotesDialogOpen(false);
                  }}
                >
                  View All Notes
                </Button>
              )}
            </div>
            <Button variant="filled" onClick={() => setNotesDialogOpen(false)}>
              Close
            </Button>
          </div>
        }
      >
        <div className="overflow-y-auto space-y-4 max-h-[calc(85vh-160px)]">
          {pinnedNotes.notes.map((note: CRMNote) => {
            const noteAction = pinnedNotes.getNoteAction?.(note);
            const priorityType = note.priority === NotePriorityValues.ALERT ? "error" : "information";
            const formattedExpiration = note.priorityExpirationDate
              ? toFormat(note.priorityExpirationDate, "MMM dd, yyyy")
              : null;
            return (
              <DetailsCard
                key={note.id}
                compact
                linked
                options={[
                  {
                    iconName: "sticky_note_2",
                    iconBackground: true,
                    overline: note.displayTitle || note.title || "Untitled Note",
                    headline: note.content,
                    supportingText: note.updatedAt ? toFormat(note.updatedAt, "MMM dd, yyyy") : null,
                    tagText:
                      note.priority && note.priority !== NotePriorityValues.NONE
                        ? formatNotePriority(note.priority)
                        : undefined,
                    tagType: note.priority && note.priority !== NotePriorityValues.NONE ? priorityType : undefined,
                    tagIconName: formattedExpiration ? "schedule" : undefined,
                    tagTooltipText: formattedExpiration ? `Expires ${formattedExpiration}` : undefined,
                    action: noteAction
                      ? () => {
                          noteAction();
                          setNotesDialogOpen(false);
                        }
                      : undefined
                  }
                ]}
              />
            );
          })}
        </div>
      </Dialog>
    );
  };

  const heading = useCompact ? (
    <div className="flex items-start gap-3">
      {avatar}
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-w-0 items-start justify-between gap-2">
          <div className="min-w-0 flex-1">{nameNode}</div>
          {overflow}
        </div>
        {compactTagRow}
        <div id={compactDetailsId} hidden={!compactDetailsVisible} data-testid="person-heading-compact-details">
          <div className="mt-2 flex flex-col items-start gap-2">
            {idAndSectionLine}
            {employmentColumn}
            {profileLinkButton}
          </div>
        </div>
        {detailsToggle ? (
          <div className={detailsOpen ? "mt-4 flex justify-start" : "mt-3 flex justify-start"}>{detailsToggle}</div>
        ) : null}
      </div>
    </div>
  ) : (
    <div
      className={
        employmentColumn
          ? "grid w-full grid-cols-[auto_minmax(0,max-content)_auto_minmax(12rem,1fr)_auto] items-start gap-x-5"
          : "grid w-full grid-cols-[auto_minmax(0,1fr)_auto] items-start gap-x-5"
      }
    >
      {avatar}
      <div className="flex min-w-0 flex-col gap-0.5">
        {identityLine}
        {idAndSectionLine}
      </div>
      {employmentColumn ? (
        <>
          <div
            className="w-px self-stretch bg-sys-brand-outline-variant"
            aria-hidden="true"
            data-testid="person-heading-col3-divider"
          />
          <div className="min-w-0">{employmentColumn}</div>
        </>
      ) : null}
      {trailingCluster}
    </div>
  );

  return (
    <div
      className={useCompact ? (sideSheet ? "w-full" : "w-full pt-7") : "w-full pt-8"}
      data-testid="person-heading-refresh"
    >
      {heading}
      {createPortal(renderNotesDialog(), document.body)}
      {canPreviewProfilePhoto
        ? createPortal(
            <ProfilePhotoPreviewDialog
              open={profilePhotoPreviewOpen}
              photoUrl={profilePhoto!}
              personName={title}
              onClose={() => setProfilePhotoPreviewOpen(false)}
              onUpdatePhoto={onImageEditClick}
            />,
            document.body
          )
        : null}
    </div>
  );
};
